import type {
  ListTemplatesResponse,
  RenderTemplateResponse,
  TemplateTargetType,
} from '@compass/client/types/template';
import { templateService } from '@services/api';

/**
 * Convenience wrappers over the generated `TemplateService`.
 *
 * The HTTP calls are now the generated `templateService.listTemplates` /
 * `renderTemplate` (BV-1874); this module only adapts the camelCase argument shapes its
 * callers already use. Snake_case query-parameter mapping is no longer done here — the
 * generated request types are snake_case, so the fields pass straight through.
 */

interface OrganizationContext {
  organizationId: string;
}

interface RenderContext {
  organizationId: string;
  programId?: string;
  caseRecordId?: string;
}

/**
 * List templates for an organization with optional filters.
 */
/** Maps camelCase option keys to the generated request's snake_case field names. */
const listTemplateFieldNames = {
  programId: 'program_id',
  workflowId: 'workflow_id',
  targetType: 'target_type',
  slug: 'slug',
  limit: 'limit',
  startAfterId: 'start_after_id',
} as const;

interface ListTemplatesOptions {
  programId?: string;
  workflowId?: string;
  targetType?: TemplateTargetType;
  slug?: string;
  limit?: number;
  startAfterId?: string;
}

export const listTemplates = async (
  context: OrganizationContext,
  options?: ListTemplatesOptions,
): Promise<ListTemplatesResponse> => {
  // Falsy values are omitted rather than sent, matching the previous behaviour: the
  // backend filters are strict exact-match, so an empty string would over-filter.
  const filters: Record<string, string | number> = {};
  for (const [optionKey, fieldName] of Object.entries(listTemplateFieldNames)) {
    const value = options?.[optionKey as keyof ListTemplatesOptions];
    if (value) {
      filters[fieldName] = value;
    }
  }

  return templateService.listTemplates({
    organization_id: context.organizationId,
    ...filters,
  });
};

/**
 * Render a template with context data.
 */
export const renderTemplate = async (
  context: RenderContext,
  templateId: string,
  templateContext?: Record<string, string>,
): Promise<RenderTemplateResponse> => {
  const { organizationId, programId, caseRecordId } = context;

  return templateService.renderTemplate({
    organization_id: organizationId,
    template_id: templateId,
    ...(programId ? { program_id: programId } : {}),
    ...(caseRecordId ? { case_record_id: caseRecordId } : {}),
    ...(templateContext ? { context: templateContext } : {}),
  });
};
